export const mock = {
  nav: [
    { label: "Home", to: "/" },
    { label: "Pricing", to: "/pricing" },
    { label: "Docs", to: "/docs" },
    { label: "Blog", to: "/blog" },
  ],
  hero: {
    headline: "Mock Services Made Simple",
    subhead:
      "Start with local mock services that can seamlessly blend with live ones. In seconds, connect secure, shareable environments for staging, demos, or long-term hosting. No infrastructure, no routing tables and system management — just code and instant collaboration.",
    ctas: [
      { label: "Get started", href: "/pricing", variant: "primary" },
      { label: "Read the docs", href: "/docs", variant: "secondary" },
    ],
    terminal: {
      title: "https://smaughk..",
      lines: [
        { kind: "cmd", text: "smaughk sync --env" },
        { kind: "out", text: "✓ env synced  •  overlay=zero-trust" },
      ],
    },
  },
  bentoFeatures: [
    {
      key: "performance",
      title: "Productivity",
      description:
        "Smaughk transforms how developers and project managers build, test and demo applications.",
      accent: "green",
      bullets: ["Build Faster", "Test Safely", "Demo Confidently"],
    },
    {
      key: "infra",
      title: "Infrastructure",
      description:
        "Define your mock services and endpoints in code, and collaborate with near zero infrastructure setup.",
      accent: "teal",
      bullets: ["Team-Ready", "Low-Friction", "Composable"],
    },
    {
      key: "workflow",
      title: "Workflow",
      description:
        "Create instant Synthetic Environments™ where your team can mix and match mock services and real services.",
      accent: "violet",
      bullets: ["Plug-and-Play", "Instant", "Flexible"],
    },
    {
      key: "deploy",
      title: "Deployment",
      description:
        "Bring Continuous Collaboration into your vocabulary without modifying existing networks.",
      accent: "amber",
      bullets: ["Network-Agnostic", "Seamless", "Frictionless"],
    },
  ],
  faqs: [
    {
      q: "What is a Synthetic Environment?",
      a: "A Synthetic Environment is a fully simulated system that behaves like your real services, APIs, and dependencies—without requiring changes to your actual infrastructure.",
    },
    {
      q: "How is Smaughk different from traditional API mocking tools?",
      a: "Traditional mocks return static responses. Smaughk creates adaptive, context-aware mocks and full synthetic environments that behave like real systems and can mix synthetic and real responses.",
    },
    {
      q: "Can Smaughk create public tunnels?",
      a: "Yes. Smaughk can create secure public tunnels that expose a synthetic environment through a managed URL, without opening inbound ports or modifying your firewall.\n\nPublic tunnels run on a zero-trust overlay networking model, meaning:\n- No direct access to your internal network\n- No inbound firewall rules\n- No exposure of real services unless explicitly configured",
    },
    {
      q: "Is Smaughk only for testing?",
      a: "No. Smaughk is designed for development, testing, and demonstrations. Many teams use it to demo systems safely without modifying production or staging environments.",
    },
    {
      q: "Do I need to change my existing services to use Smaughk?",
      a: "No. Smaughk is designed to sit alongside or in front of existing services, allowing you to mock, overlay, or simulate behavior without modifying service code.",
    },
  ],
  pricing: {
    tiers: [
      {
        id: "free",
        name: "Free",
        price: "$0",
        period: "/mo",
        tagline: "For individual hackers",
        highlight: false,
        features: [
          "5 Synthetic Environments™",
          "Unlimited Mock Services",
          "Unlimited Real Services",
          "Community Support",
        ],
        cta: "Start free",
      },
      {
        id: "pro",
        name: "Group",
        price: "$20",
        period: "/mo",
        tagline: "Collaborate and thrive.",
        highlight: true,
        features: [
          "Everything in Free",
          "Connect 20+ users to Synthetic Environments™",
          "5 Free Public Tunnels",
          "Discord Chat Support",
        ],
        cta: "Connect a Group",
      },
      {
        id: "enterprise",
        name: "Enterprise",
        price: "Custom",
        period: "",
        tagline: "Security + scale",
        highlight: "enterprise",
        features: ["SSO", "Dedicated support", "Custom regions", "Audit logs"],
        cta: "Talk to sales",
      },
    ],
    estimator: {
      min: 0,
      max: 100,
      step: 1,
      defaultValue: 25,
      unitLabel: "Compute units",
      pricePerUnit: 0.18,
      includedUnits: 60,
    },
  },
  changelog: [
    {
      id: "cl-2025-08-18",
      date: "2025-08-18",
      title: "Edge deployment previews",
      type: "Feature",
      body:
        "Preview URLs now come with region-aware routing and immutable hashes. Deploy, test, and ship without guesswork.",
      git: "9f2c7a1",
      screenshotHint: "UI screenshot placeholder",
    },
    {
      id: "cl-2025-08-10",
      date: "2025-08-10",
      title: "Faster run summaries",
      type: "Improvement",
      body:
        "Summaries now stream as metrics arrive. Less waiting, more iteration.",
      git: "a1c8d02",
      screenshotHint: "UI screenshot placeholder",
    },
    {
      id: "cl-2025-08-02",
      date: "2025-08-02",
      title: "Fixed flaky websocket reconnects",
      type: "Fix",
      body:
        "Improved backoff strategy under high churn to reduce duplicate sessions.",
      git: "c3b44ef",
      screenshotHint: "UI screenshot placeholder",
    },
  ],
  docs: {
    tree: [
      {
        group: "Getting Started",
        items: [
          { id: "intro", title: "Introduction" },
          { id: "install", title: "Install" },
          { id: "first-run", title: "Your first run" },
        ],
      },
      {
        group: "Core",
        items: [
          { id: "config", title: "Config file" },
          { id: "cli", title: "CLI" },
          { id: "metrics", title: "Metrics" },
        ],
      },
      {
        group: "Deploy",
        items: [
          { id: "edge", title: "Edge runtime" },
          { id: "rollbacks", title: "Rollbacks" },
        ],
      },
    ],
    pages: {
      intro: {
        title: "Introduction",
        content:
          "Keep mocks and real services in Code as Infrastructure. The docs playground on the right is a simulated output, to explain concepts easier.",
      },
      install: {
        title: "Install",
        content:
          "Install via your package manager of choice, then run `bolt init` to scaffold a project. Add the config below and run your first test.",
      },
      "first-run": {
        title: "Your first run",
        content:
          "Start small: 50 VUs for 30 seconds. Watch p95 and errors. Then scale.",
      },
      config: {
        title: "Config file",
        content:
          "The config is intentionally boring. Determinism beats magic. Update the left pane and watch the simulated summary update.",
      },
      cli: {
        title: "CLI",
        content:
          "The CLI is keyboard-first. Use it locally, in CI, or wire it into any orchestrator.",
      },
      metrics: {
        title: "Metrics",
        content:
          "We default to p95 and error rate — the two numbers that keep teams honest.",
      },
      edge: {
        title: "Edge runtime",
        content:
          "Deploy to edge regions to get realistic latency, then iterate quickly with previews.",
      },
      rollbacks: {
        title: "Rollbacks",
        content:
          "Roll back by hash. Everything is immutable and auditable.",
      },
    },
    starterConfig: `# smaughk.mocks.hcl\n\nservice "weather-service" {\n  route  = "/temp/{id}"\n  method = "GET"\n\n  response {\n    status = 200\n    body   = { id = 123, name = "Cloudy with a chance of brilliance" }\n  }\n}\n`,
  },
  blog: [
    {
      id: "zero-trust-overlay",
      date: "2025-08-22",
      title: "What is a zero-trust overlay network?",
      excerpt:
        "A quick explainer: secure connectivity without inbound ports, firewall edits, or implicit trust.",
      readingTime: "2 min",
      tags: ["Networking", "Zero-Trust"],
      body:
        "A zero-trust overlay network is a secure networking layer built on top of existing networks (your LAN, cloud VPCs, the public internet). Instead of assuming anything inside a perimeter is trusted, every connection is explicitly authenticated and authorized.\n\nIn practice, this means you can connect services and environments without opening inbound ports or modifying firewall rules. Access is granted per identity and policy, and services are only exposed when you intentionally configure them.\n\nFor Smaughk, this model enables shareable Synthetic Environments™: your team can collaborate through managed URLs while keeping your real network private by default.",
    },
  ],
}
